import { TFunction } from 'i18next';
import BinderDeployment from 'components/features/binder-deployment';
import Binding from 'components/features/binding';
import CatRating from 'components/features/cat-rating';
import FireRating from 'components/features/fire-rating';
import LayerResults from 'components/features/layer-results';
import PeerReview from 'components/features/peer-review';
import QuoteGeneration from 'components/features/quote-generation';
import ScheduleOfValues from 'components/features/schedule-of-values';
import SubmissionDetails from 'components/features/submission-details';
import SubmissionRegistration from 'components/features/submission-registration';
import {
  BusinessLineResponseDtoNameEnum,
  SubmissionResponseDto,
  SubmissionResponseDtoStatusEnum,
} from 'libs/pinnacle-sdk';
import CloseSubmission from 'components/features/close-submission';
import { EPath } from 'constants/path';
import { BUSINESS_LINE_FOR_PRICING_MODEL } from 'constants/price-rating';

export interface SubmissionTabProps {
  submission: SubmissionResponseDto;
  includeNext: boolean;
  includeBack: boolean;
  navigateNext: (step?: number) => void;
  navigateBack: (step?: number) => void;
}

export const NUMBER_OF_SUBMISSION_HEADER_TABS = 10;

export const getSubmissionConfigTabs = (
  submissionStatus: SubmissionResponseDtoStatusEnum | undefined,
  businessLine: BusinessLineResponseDtoNameEnum | undefined,
  isReadyForReview = false,
  t: TFunction
) => {
  const shouldDisableTab =
    !businessLine || !BUSINESS_LINE_FOR_PRICING_MODEL.includes(businessLine);
  return [
    {
      id: 0,
      tab: t('submission.navigation.submissionRegistration'),
      component: SubmissionRegistration,
    },
    {
      id: 1,
      tab: t('submission.navigation.submissionDetails'),
      component: SubmissionDetails,
    },
    {
      id: 2,
      tab: t('submission.navigation.scheduleOfValues'),
      disabled: shouldDisableTab,
      component: ScheduleOfValues,
    },
    {
      id: 3,
      tab: t('submission.navigation.fireRating'),
      component: FireRating,
      disabled: shouldDisableTab,
    },
    {
      id: 4,
      tab: t('submission.navigation.catRating'),
      component: CatRating,
      disabled: shouldDisableTab,
    },
    {
      id: 5,
      tab: t('submission.navigation.layerResults'),
      disabled: shouldDisableTab,
      component: LayerResults,
    },
    {
      id: 6,
      tab: t('submission.navigation.binderDeployment'),
      component: BinderDeployment,
    },
    {
      id: 7,
      tab: t('submission.navigation.peerReview'),
      component: PeerReview,
      disabled:
        submissionStatus === SubmissionResponseDtoStatusEnum.Declined ||
        submissionStatus === SubmissionResponseDtoStatusEnum.Cancelled ||
        !isReadyForReview,
    },
    {
      id: 8,
      tab: t('submission.navigation.quoteGeneration'),
      disabled: true,
      component: QuoteGeneration,
    },
    {
      id: 9,
      tab: t('submission.navigation.binding'),
      disabled:
        submissionStatus !== SubmissionResponseDtoStatusEnum.Quoted &&
        submissionStatus !== SubmissionResponseDtoStatusEnum.Complete,
      component: Binding,
    },
    {
      id: EPath.CLOSE_SUBMISSION.replace('/', ''),
      hide: true,
      component: CloseSubmission,
    },
  ];
};

export const getComponentByTabId = (
  tabId: number | string,
  businessLine: BusinessLineResponseDtoNameEnum | undefined,
  t: TFunction
): React.FC<SubmissionTabProps> => {
  const configTabs = getSubmissionConfigTabs(undefined, businessLine, false, t);
  const tab = configTabs.find((tab) => tab.id === tabId);
  return tab ? tab.component : configTabs[0].component;
};
